import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Home, 
  Calendar, 
  Users, 
  DollarSign, 
  MapPin,
  Mail,
  Phone,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getPublicBookingRequestStatus } from '@/utils/businessAppApi';

type RequestStatus = 'pending' | 'under_review' | 'accepted' | 'rejected' | 'modified' | 'paid' | 'completed';

interface BookingRequestStatus {
  requestNumber: string;
  status: RequestStatus;
  adventureName: string;
  businessName: string;
  businessContact: {
    email: string;
    phone?: string;
  };
  dateRange: {
    startDate: string;
    endDate: string;
    flexibility: string;
  };
  participants: {
    adults: number;
    children: number;
    students: number;
  };
  estimatedPrice: {
    totalEstimate: number;
    currency: string;
  };
  finalPrice?: {
    total: number;
    currency: string;
  };
  customerDetails: {
    name: string;
    email: string;
    phone: string;
    specialRequests?: string;
  };
  selectedOptions: Record<string, any>;
  createdAt: string;
  lastUpdated: string;
  statusHistory: {
    status: RequestStatus;
    timestamp: string;
    note?: string;
  }[];
  businessResponse?: {
    message?: string;
    proposedChanges?: {
      dateRange?: { startDate: string; endDate: string };
      pricing?: { amount: number; reason: string };
    };
    deadline?: string;
  };
}

export default function BookingStatus() {
  const navigate = useNavigate();
  const { requestNumber } = useParams<{ requestNumber: string }>();
  const { t } = useTranslation('public', { keyPrefix: 'customAdventures.tracking' });
  const { t: tCommon } = useTranslation('common');
  
  const [requestStatus, setRequestStatus] = useState<BookingRequestStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRequestStatus = async () => {
    if (!requestNumber) return;
    
    try {
      console.log('Fetching status for:', requestNumber);
      console.log('API call will be made to:', `/custom-adventure-bookings/public/${requestNumber}`);
      
      // Fetch real data from API
      const data = await getPublicBookingRequestStatus(requestNumber);
      
      console.log('API response data:', data);
      console.log('Date fields:', {
        startDate: data.requestedDateRange.startDate,
        startDateType: typeof data.requestedDateRange.startDate,
        endDate: data.requestedDateRange.endDate,
        endDateType: typeof data.requestedDateRange.endDate,
        createdAt: data.createdAt,
        createdAtType: typeof data.createdAt
      });
      console.log('Business response:', data.businessResponse);
      if (data.businessResponse) {
        console.log('Business response details:', {
          respondedAt: data.businessResponse.respondedAt,
          respondedAtType: typeof data.businessResponse.respondedAt,
          response: data.businessResponse.response,
          message: data.businessResponse.message
        });
      }
      
      // Transform API data to component format
      const transformedStatus: BookingRequestStatus = {
        requestNumber: data.requestNumber,
        status: data.status as RequestStatus,
        adventureName: data.customAdventure?.name || 'Custom Adventure',
        businessName: data.customAdventure?.businessName || 'Adventure Provider',
        businessContact: {
          email: data.customAdventure?.businessContact?.email || 'info@business.com',
          phone: data.customAdventure?.businessContact?.phone || '+1 555-0000'
        },
        dateRange: {
          startDate: (() => {
            try {
              return typeof data.requestedDateRange.startDate === 'string' 
                ? data.requestedDateRange.startDate 
                : new Date(data.requestedDateRange.startDate).toISOString();
            } catch (e) {
              console.error('Error parsing startDate:', e);
              return new Date().toISOString();
            }
          })(),
          endDate: (() => {
            try {
              return typeof data.requestedDateRange.endDate === 'string' 
                ? data.requestedDateRange.endDate 
                : new Date(data.requestedDateRange.endDate).toISOString();
            } catch (e) {
              console.error('Error parsing endDate:', e);
              return new Date().toISOString();
            }
          })(),
          flexibility: data.requestedDateRange.flexibility
        },
        participants: {
          adults: data.participants?.adults || 0,
          children: data.participants?.children || 0,
          students: data.participants?.students || 0
        },
        estimatedPrice: {
          totalEstimate: data.estimatedPrice.totalEstimate,
          currency: data.estimatedPrice.currency
        },
        customerDetails: {
          name: data.customerDetails?.name || 'Customer',
          email: data.customerDetails?.email || 'customer@email.com',
          phone: data.customerDetails?.phone || '+1 555-0123',
          specialRequests: data.customerDetails?.specialRequests
        },
        selectedOptions: data.selectedOptions || {},
        createdAt: typeof data.createdAt === 'string' 
          ? data.createdAt 
          : new Date(data.createdAt).toISOString(),
        lastUpdated: (() => {
          if (data.businessResponse?.respondedAt) {
            try {
              return typeof data.businessResponse.respondedAt === 'string' 
                ? data.businessResponse.respondedAt 
                : new Date(data.businessResponse.respondedAt).toISOString();
            } catch (e) {
              console.error('Error parsing businessResponse.respondedAt:', e);
            }
          }
          return typeof data.createdAt === 'string' 
            ? data.createdAt 
            : new Date(data.createdAt).toISOString();
        })(),
        statusHistory: [
          {
            status: 'pending',
            timestamp: typeof data.createdAt === 'string' 
              ? data.createdAt 
              : new Date(data.createdAt).toISOString(),
            note: 'Request submitted successfully'
          }
        ],
        businessResponse: data.businessResponse ? {
          message: data.businessResponse.message,
          deadline: data.expiresAt 
            ? (typeof data.expiresAt === 'string' 
                ? data.expiresAt 
                : new Date(data.expiresAt).toISOString())
            : undefined
        } : undefined
      };
      
      // Add business response to status history if available
      if (data.businessResponse && data.businessResponse.respondedAt) {
        let responseTimestamp;
        try {
          if (typeof data.businessResponse.respondedAt === 'string') {
            responseTimestamp = data.businessResponse.respondedAt;
          } else if (data.businessResponse.respondedAt) {
            responseTimestamp = new Date(data.businessResponse.respondedAt).toISOString();
          } else {
            responseTimestamp = new Date().toISOString(); // fallback to current time
          }
        } catch (dateError) {
          console.error('Error parsing business response date:', dateError);
          responseTimestamp = new Date().toISOString(); // fallback to current time
        }

        transformedStatus.statusHistory.push({
          status: data.businessResponse.response === 'accept' ? 'accepted' : 
                 data.businessResponse.response === 'decline' ? 'rejected' : 'modified',
          timestamp: responseTimestamp,
          note: data.businessResponse.message || 
                `Business ${data.businessResponse.response}ed your request`
        });
      }
      
      setRequestStatus(transformedStatus);
    } catch (error) {
      console.error('Error fetching request status:', error);
      console.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      
      // If request not found, show fallback mock data for demonstration
      if (error instanceof Error && error.message.includes('not found')) {
        console.log('Request not found, showing mock data for demo');
        const mockStatus: BookingRequestStatus = {
          requestNumber,
          status: 'accepted',
          adventureName: 'Aventura en 4x4',
          businessName: 'Adventure Tours Costa Rica',
          businessContact: {
            email: 'info@adventuretours.cr',
            phone: '+506 8888-9999'
          },
          dateRange: {
            startDate: '2025-03-15',
            endDate: '2025-03-17',
            flexibility: 'flexible_1_week'
          },
          participants: {
            adults: 2,
            children: 1,
            students: 0
          },
          estimatedPrice: {
            totalEstimate: 450,
            currency: 'USD'
          },
          customerDetails: {
            name: 'Mario Lallana',
            email: 'mario_lallana_12@hotmail.com',
            phone: '+1 555-0123'
          },
          selectedOptions: {
            'Transportation': 'Private Vehicle',
            'Accommodation': 'Standard',
            'Meals': 'All Inclusive',
            'Group Size': 'Small Group (4-8 people)'
          },
          createdAt: '2025-01-30T17:17:27.923Z',
          lastUpdated: '2025-01-30T17:17:27.923Z',
          statusHistory: [
            {
              status: 'pending',
              timestamp: '2025-01-30T17:17:27.923Z',
              note: 'Request submitted successfully'
            },
            {
              status: 'under_review',
              timestamp: '2025-01-30T17:30:00.000Z',
              note: 'Business received your request and is reviewing availability'
            },
            {
              status: 'accepted',
              timestamp: '2025-01-30T18:00:00.000Z',
              note: 'Great news! Your adventure request has been accepted!'
            }
          ],
          businessResponse: {
            message: "Great news! We'd love to host your undefined adventure. We'll be in touch soon to finalize the details. desde LOCAL",
            deadline: '2025-02-15T23:59:59.000Z'
          }
        };
        
        setRequestStatus(mockStatus);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRequestStatus();
  }, [requestNumber]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchRequestStatus();
  };

  const getStatusConfig = (status: RequestStatus) => {
    switch (status) {
      case 'pending':
        return {
          icon: Clock,
          color: 'text-amber-600',
          bgColor: 'bg-amber-100',
          borderColor: 'border-amber-200',
          label: t('status.pending')
        };
      case 'under_review':
        return {
          icon: Clock,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          borderColor: 'border-blue-200',
          label: t('status.underReview')
        };
      case 'accepted':
        return {
          icon: CheckCircle2,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-200',
          label: t('status.accepted')
        };
      case 'rejected':
        return {
          icon: XCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          borderColor: 'border-red-200',
          label: t('status.rejected')
        };
      case 'modified':
        return {
          icon: AlertTriangle,
          color: 'text-orange-600',
          bgColor: 'bg-orange-100',
          borderColor: 'border-orange-200',
          label: t('status.modified')
        };
      case 'paid':
        return {
          icon: CheckCircle2,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-200',
          label: t('status.paid')
        };
      case 'completed':
        return {
          icon: CheckCircle2,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-200',
          label: t('status.completed')
        };
      default:
        return {
          icon: Clock,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-200',
          label: status
        };
    }
  };

  const getTotalParticipants = () => {
    if (!requestStatus) return 0;
    return requestStatus.participants.adults + requestStatus.participants.children + requestStatus.participants.students;
  };

  const getFlexibilityText = (flexibility: string) => {
    switch (flexibility) {
      case 'exact':
        return t('flexibility.exact');
      case 'flexible_1_week':
        return t('flexibility.oneWeek');
      case 'flexible_2_weeks':
        return t('flexibility.twoWeeks');
      case 'flexible_1_month':
        return t('flexibility.oneMonth');
      default:
        return flexibility;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#77A146]" />
      </div>
    );
  }

  if (!requestStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('notFound.title')}</h1>
          <p className="text-gray-600 mb-4">{t('notFound.description')}</p>
          <Button onClick={() => navigate('/')} className="bg-[#77A146] hover:bg-[#6a9139]">
            <Home className="h-4 w-4 mr-2" />
            {t('notFound.backHome')}
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(requestStatus.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-[#F0F4F0] p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="mb-4 bg-white hover:bg-gray-50"
            >
              <Home className="h-4 w-4 mr-2" />
              {tCommon('actions.backHome')}
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('title')}</h1>
            <p className="text-gray-600">{t('subtitle', { requestNumber })}</p>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            disabled={refreshing}
            className="bg-white hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('actions.refresh')}
          </Button>
        </div>

        {/* Current Status Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="text-xl">{t('currentStatus.title')}</span>
              <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0 px-4 py-2`}>
                <StatusIcon className="h-4 w-4 mr-2" />
                {statusConfig.label}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Adventure Info */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-[#77A146]" />
                  {t('sections.adventure')}
                </h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">{t('fields.adventureName')}</p>
                    <p className="font-medium">{requestStatus.adventureName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('fields.business')}</p>
                    <p className="font-medium">{requestStatus.businessName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('fields.requestDate')}</p>
                    <p className="font-medium">{format(new Date(requestStatus.createdAt), 'MMMM d, yyyy')}</p>
                  </div>
                </div>
              </div>

              {/* Status Info */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Clock className="h-5 w-5 mr-2 text-[#77A146]" />
                  {t('sections.statusInfo')}
                </h3>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-gray-500">{t('fields.lastUpdated')}</p>
                    <p className="font-medium">{format(new Date(requestStatus.lastUpdated), 'MMMM d, yyyy - HH:mm')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('fields.expectedResponse')}</p>
                    <p className="font-medium">{t('expectedResponse.within48h')}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Request Details */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Dates & Participants */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-[#77A146]" />
                {t('sections.dateParticipants')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">{t('fields.dateRange')}</p>
                  <p className="font-medium">
                    {format(new Date(requestStatus.dateRange.startDate), 'MMM d, yyyy')} - {' '}
                    {format(new Date(requestStatus.dateRange.endDate), 'MMM d, yyyy')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('fields.flexibility')}</p>
                  <p className="font-medium">{getFlexibilityText(requestStatus.dateRange.flexibility)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('fields.participants')}</p>
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    <span className="font-medium">{getTotalParticipants()} {t('fields.total')}</span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600 space-y-1">
                    {requestStatus.participants.adults > 0 && (
                      <div>• {requestStatus.participants.adults} {t('fields.adults')}</div>
                    )}
                    {requestStatus.participants.children > 0 && (
                      <div>• {requestStatus.participants.children} {t('fields.children')}</div>
                    )}
                    {requestStatus.participants.students > 0 && (
                      <div>• {requestStatus.participants.students} {t('fields.students')}</div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-[#77A146]" />
                {t('sections.pricing')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">{t('fields.estimatedPrice')}</p>
                  <p className="text-2xl font-bold text-[#77A146]">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: requestStatus.estimatedPrice.currency
                    }).format(requestStatus.estimatedPrice.totalEstimate)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{t('fields.priceNote')}</p>
                </div>
                
                {requestStatus.finalPrice && (
                  <div className="pt-3 border-t">
                    <p className="text-sm text-gray-500">{t('fields.finalPrice')}</p>
                    <p className="text-2xl font-bold text-green-600">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: requestStatus.finalPrice.currency
                      }).format(requestStatus.finalPrice.total)}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Business Contact */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('sections.businessContact')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center">
                <Mail className="h-4 w-4 mr-2 text-gray-400" />
                <a 
                  href={`mailto:${requestStatus.businessContact.email}`}
                  className="text-[#77A146] hover:underline"
                >
                  {requestStatus.businessContact.email}
                </a>
              </div>
              {requestStatus.businessContact.phone && (
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2 text-gray-400" />
                  <a 
                    href={`tel:${requestStatus.businessContact.phone}`}
                    className="text-[#77A146] hover:underline"
                  >
                    {requestStatus.businessContact.phone}
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Customer Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('sections.customerDetails')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">{t('fields.customerName')}</p>
                <p className="font-medium text-gray-900">{requestStatus.customerDetails.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.email')}</p>
                <p className="font-medium text-gray-900">{requestStatus.customerDetails.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('fields.phone')}</p>
                <p className="font-medium text-gray-900">{requestStatus.customerDetails.phone}</p>
              </div>
              {requestStatus.customerDetails.specialRequests && (
                <div className="md:col-span-3">
                  <p className="text-sm text-gray-500">{t('fields.specialRequests')}</p>
                  <p className="font-medium text-gray-900">{requestStatus.customerDetails.specialRequests}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Selected Options */}
        {Object.keys(requestStatus.selectedOptions).length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('sections.selectedOptions')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(requestStatus.selectedOptions).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b border-gray-100 last:border-b-0">
                    <span className="text-gray-600">{key}:</span>
                    <span className="font-medium text-gray-900">{String(value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Section - Only show when request is accepted */}
        {requestStatus.status === 'accepted' && (
          <Card className="mb-6 border-2 border-[#77A146] bg-gradient-to-r from-emerald-50 to-emerald-50">
            <CardHeader>
              <CardTitle className="flex items-center text-[#77A146]">
                <CheckCircle2 className="h-5 w-5 mr-2" />
                {t('payment.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <h3 className="font-semibold text-gray-900 mb-2">{t('payment.congratulations')}</h3>
                  <p className="text-gray-600 mb-4">{t('payment.description')}</p>
                  
                  {/* Payment deadline */}
                  {requestStatus.businessResponse?.deadline && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-amber-800">
                        <strong>{t('payment.deadline')}:</strong> {' '}
                        {format(new Date(requestStatus.businessResponse.deadline), 'MMMM d, yyyy')}
                      </p>
                    </div>
                  )}
                  
                  {/* Final price display */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('payment.finalAmount')}:</span>
                      <span className="text-2xl font-bold text-[#77A146]">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: requestStatus.estimatedPrice.currency
                        }).format(requestStatus.estimatedPrice.totalEstimate)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Payment button */}
                  <Button 
                    onClick={() => {
                      // Navigate to payment page with data
                      const paymentData = {
                        requestNumber: requestStatus.requestNumber,
                        adventureName: requestStatus.adventureName,
                        businessName: requestStatus.businessName,
                        dateRange: {
                          startDate: requestStatus.dateRange.startDate,
                          endDate: requestStatus.dateRange.endDate
                        },
                        participants: requestStatus.participants,
                        finalPrice: {
                          total: requestStatus.estimatedPrice.totalEstimate,
                          currency: requestStatus.estimatedPrice.currency,
                          breakdown: {
                            basePrice: requestStatus.estimatedPrice.totalEstimate, // This would be more detailed in real data
                            options: 0
                          }
                        },
                        customerDetails: requestStatus.customerDetails,
                        selectedOptions: requestStatus.selectedOptions
                      };
                      
                      navigate('/custom-adventure-payment', {
                        state: { adventureData: paymentData }
                      });
                    }}
                    className="w-full bg-[#77A146] hover:bg-[#6a9139] text-white py-3 text-lg font-semibold"
                  >
                    {t('payment.proceedButton')} →
                  </Button>
                </div>
                
                {/* Business response message */}
                {requestStatus.businessResponse?.message && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">{t('payment.businessMessage')}</h4>
                    <p className="text-blue-800 text-sm italic">"{requestStatus.businessResponse.message}"</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status History */}
        <Card>
          <CardHeader>
            <CardTitle>{t('sections.statusHistory')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {requestStatus.statusHistory.map((entry, index) => {
                const config = getStatusConfig(entry.status);
                const EntryIcon = config.icon;
                
                return (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${config.bgColor}`}>
                      <EntryIcon className={`h-4 w-4 ${config.color}`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="font-medium">{config.label}</p>
                        <p className="text-sm text-gray-500">
                          {format(new Date(entry.timestamp), 'MMM d, yyyy - HH:mm')}
                        </p>
                      </div>
                      {entry.note && (
                        <p className="text-sm text-gray-600 mt-1">{entry.note}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
